import {useEffect, useRef} from 'react'
import type {GenericContent, GenericCTAGroup} from '../../../../../../brand/lib/types/contentful'
import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {animateFadeIn, type AnimationElement} from '../../_utils/animations'

import {Heading, Text} from '@primer/react-brand'
import {CtaButtons} from '../../_shared/CtaButtons/CtaButtons'
import {Label} from '../../_shared/Label/Label'
import {Spacer} from '../../_shared/Spacer/Spacer'
import {ShareButton} from '../../_shared/ShareButton/ShareButton'
import {Mascot} from '../../_shared/Mascot/Mascot'

import Styles from './Hero.module.css'

type Props = {
  content: GenericContent
  location: string
  sectionId?: string
  sectionIndex: number
}

export function Hero(props: Props) {
  const {content, location, sectionId, sectionIndex} = props
  const {heading, subheading, label, text, ctas} = content.fields

  const title = heading
  const subTitle = subheading ? documentToPlainTextString(subheading) : ''

  const eyebrow = label?.fields.text
  const description = text ? documentToPlainTextString(text) : ''
  const ctaGroup = ctas?.at(0) as GenericCTAGroup

  const mascot = content.fields.content?.at(0)?.fields.id || '' // ID is used for choosing mascot

  const wrapperRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLDivElement>(null)
  const descriptionRef = useRef<HTMLDivElement>(null)
  const ctaRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const elements: AnimationElement[] = []

    if (titleRef.current) elements.push({element: titleRef.current})
    if (descriptionRef.current) elements.push({element: descriptionRef.current})
    if (ctaRef.current) elements.push({element: ctaRef.current})

    if (wrapperRef.current) {
      return animateFadeIn(elements, wrapperRef.current)
    }
  }, [])

  return (
    <div className={Styles.Hero} ref={wrapperRef}>
      <div className={Styles.Cell}>
        {subTitle && <Label className={Styles.SubTitle} text={subTitle} hideIcon />}
        {eyebrow && <Label className={Styles.EyebrowMobile} text={eyebrow} aria-hidden="true" />}

        {title && (
          <Heading data-heading="1" as={sectionIndex === 0 ? 'h1' : 'h2'} ref={titleRef}>
            {title}
          </Heading>
        )}

        {sectionId && sectionIndex > 0 && <ShareButton id={sectionId} location={location} />}
      </div>

      <div className={Styles.Cell}>
        {eyebrow && <Label className={Styles.EyebrowDesktop} text={eyebrow} />}

        <div>
          {description && (
            <div ref={descriptionRef}>
              <Text as="p" className={Styles.Description}>
                {description}
              </Text>
            </div>
          )}

          {ctas && (
            <>
              <Spacer size="32px" size1280="64px" />
              <div ref={ctaRef}>
                <CtaButtons contentfulContent={ctaGroup} location={location} />
              </div>
            </>
          )}
        </div>

        {/* Mascot */}
        {mascot && <Mascot mascot={mascot} />}
      </div>
    </div>
  )
}

try{ Hero.displayName ||= 'Hero' } catch {}